import React, { useContext, useState } from "react";
import { useMutation } from "@apollo/client";
import { message as antdMessage } from "antd";
import { LuRotateCw, LuInfo } from "react-icons/lu";
import { CREATE_EMAIL } from "../../../../graphql/mutation";
import { UserContext } from "../../../../amplify/authenticator-provider";
import { EMAIL_STATUS_ENUMS } from "../../../../constant/EmailConstant";
import CustomButton from "../../../commonComponents/button";

/**
 * Shared "this email bounced — resend it" affordance used by the activity
 * center message thread, the email-tab thread detail, and the email-tab list
 * card. A sent email can come back BOUNCE (recipient mailbox rejected it);
 * only outbound (TO_LEAD) mail carries an actionable deliverability status.
 *
 * Self-gating: renders ``null`` unless the email is an outbound bounce, so
 * callers can drop it in unconditionally and pass their own field mapping.
 *
 * @param {{
 *   direction?: string,            // message direction; resend shows only for "TO_LEAD"
 *   status?: string,               // email deliverability status; shows only on BOUNCE
 *   to?: string,                   // recipient — may be "Name <email>" form
 *   subject?: string,
 *   content?: string,              // email body html to resend
 *   leadId?: number|string,
 *   leadOpportunityId?: number|string|null,
 *   onResent?: (createdMessage:any) => void, // called after a successful resend
 *   className?: string,            // extra classes on the wrapper
 * }} props
 */
const BouncedEmailResend = ({
  direction,
  status,
  to,
  subject,
  content,
  leadId,
  leadOpportunityId,
  onResent,
  className = "",
  // When true, render just the Resend button (with an explanatory tooltip)
  // and omit the inline info note — used by the compact email-list card.
  buttonOnly = false,
}) => {
  // @ts-ignore
  const { user: currentUser } = useContext(UserContext);
  const [createEmail] = useMutation(CREATE_EMAIL);
  const [isResending, setIsResending] = useState(false);

  // Only outbound (TO_LEAD) mail that bounced is resendable — we never
  // resend an inbound email. Bounce is an outbound-only deliverability
  // status, so this is the correct and intended gate.
  const isBouncedSentEmail =
    (direction === "TO_LEAD" || direction === "MessageDirection.TO_LEAD") &&
    status === EMAIL_STATUS_ENUMS.BOUNCE;
  if (!isBouncedSentEmail) return null;

  const handleResend = async e => {
    // Don't let the click bubble to an enclosing handler (e.g. the list
    // card's select-email onClick).
    e?.stopPropagation?.();
    // ``to`` can be a display-name form like
    // "Ethan Test-EAI <ethanf@engagedai.io>" — pull out the address inside
    // the angle brackets; fall back to the raw value when there are none.
    const rawTo = to || "";
    const recipient = (rawTo.match(/<([^>]+)>/)?.[1] || rawTo).trim();
    const resolvedLeadId = +(leadId || 0);
    if (!recipient || !resolvedLeadId) {
      antdMessage.error("Cannot resend: missing recipient or lead.");
      return;
    }
    try {
      setIsResending(true);
      const res = await createEmail({
        variables: {
          content: content || "",
          subject: subject || "",
          to: recipient,
          leadId: resolvedLeadId,
          leadOpportunityId: leadOpportunityId || null,
          userId: currentUser?.id,
        },
      });
      if (res?.data?.createEmail?.statusCode === 200) {
        antdMessage.success(res.data.createEmail.message || "Email resent successfully.");
        onResent?.(res.data.createEmail.leadMessage);
      } else {
        antdMessage.error(res?.data?.createEmail?.message || "Failed to resend email.");
      }
    } catch (err) {
      antdMessage.error("Failed to resend email. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const resendButton = (
    <CustomButton
      type="link"
      danger
      size="small"
      loading={isResending}
      icon={<LuRotateCw />}
      handleClick={handleResend}
      className={`text-xs font-semibold ${buttonOnly ? "!h-auto !p-0 leading-none" : "px-1"}`}
      tooltipTitle="This email bounced. Click to resend it."
      btnText="Resend"
    />
  );

  // Button-only mode (compact list card): no inline note, the tooltip carries
  // the explanation. Wrapper still stops propagation so the resend click
  // doesn't trigger an enclosing row handler (e.g. select-email).
  if (buttonOnly) {
    return (
      <span className={className} onClick={e => e.stopPropagation()}>
        {resendButton}
      </span>
    );
  }

  return (
    <span
      className={`flex items-center gap-1 text-xs text-warning-500 ${className}`}
      onClick={e => e.stopPropagation()}
    >
      <LuInfo className="shrink-0" />
      <span>This email bounced. Click to resend it.</span>
      {resendButton}
    </span>
  );
};

export default BouncedEmailResend;
